// Always name keys with uppercase snake_casing
// Always use keys for data storage

import constants from 'vets-json-schema/dist/constants.json';

export const dicOptions = {
  DIC: 'D.I.C.',
  '1151DIC': 'D.I.C. under U.S.C. 1151',
  pactActDIC: 'D.I.C. re-evaluation based on the PACT Act',
};

export const servicesOptions = [
  { value: 'army', label: 'Army' },
  { value: 'navy', label: 'Navy' },
  { value: 'airForce', label: 'Air Force' },
  { value: 'coastGuard', label: 'Coast Guard' },
  { value: 'marineCorps', label: 'Marine Corps' },
  { value: 'spaceForce', label: 'Space Force' },
  { value: 'usphs', label: 'USPHS' },
  { value: 'noaa', label: 'NOAA' },
];

export const claimantRelationshipOptions = {
  SURVIVING_SPOUSE: 'I’m a Veteran’s surviving spouse',
  CUSTODIAN_FILING_FOR_CHILD_UNDER_18:
    'I’m a custodian filing for a Veteran’s child who is under 18',
  'CHILD_18-23_IN_SCHOOL':
    'I’m a Veteran’s adult child who is 18-23 years old and still in school',
  HELPLESS_ADULT_CHILD: 'I’m a Veteran’s adult child who is seriously disabled',
  OTHER: 'None of these options apply to me',
};

export const marriageEndOptions = {
  death: "Spouse's death",
  divorce: 'Divorce',
  other: 'Other',
};

export const marriageTypeOptions = {
  ceremonial:
    'In a civil or religious ceremony with an officiant who signed my marriage license',
  other: 'Some other way',
};

export const separationReasonOptions = {
  MEDICAL_FINANCIAL: 'Medical or financial reasons',
  RELATIONSHIP_DIFFERENCES: 'Relationship differences or problems',
  OTHER: 'Other',
};

export const previousMarriageEndOptions = {
  DEATH: 'Death',
  DIVORCE: 'Divorce',
  OTHER: 'Other',
};

export const remarriageEndOptions = {
  death: "Spouse's death",
  divorce: 'Divorce',
  other: 'Other',
};

export const bankAccountTypeOptions = {
  CHECKING: 'Checking',
  SAVINGS: 'Savings',
};

export const incomeRecipientTypeLabels = {
  SURVIVING_SPOUSE: 'Surviving spouse',
  CHILD: 'Veteran’s child',
};

export const medicalExpenseRecipientLabels = {
  VETERAN: 'Veteran (only select if a last or burial expense)',
  SURVIVING_SPOUSE: 'Surviving spouse',
  CHILD: 'Veteran’s child',
};

export const careTypeLabels = {
  CARE_FACILITY: 'Residential care facility',
  IN_HOME_CARE_ATTENDANT: 'In-home care attendant',
};

export const allCareTypeLabels = {
  NURSING_HOME: 'Nursing home',
  CARE_FACILITY: 'Residential care facility',
  ADULT_DAYCARE: 'Adult daycare',
  IN_HOME_CARE_ATTENDANT: 'In-home care attendant',
};

export const getCareTypeLabels = formData =>
  formData?.survivorsBenefitsForm2025VersionEnabled
    ? allCareTypeLabels
    : careTypeLabels;

export const frequencyLabels = {
  MONTHLY: 'Once a month',
  ANNUALLY: 'Once a year',
  ONE_TIME: 'One-time',
};

export const careFrequencyLabels = {
  MONTHLY: 'Once a month',
  ANNUALLY: 'Once a year',
};

export const typeOfIncomeLabels = {
  SOCIAL_SECURITY: 'Social Security',
  INTEREST_DIVIDENDS: 'Interest or dividend income',
  CIVIL_SERVICE: 'Civil Service',
  PENSION_RETIREMENT: 'Pension or retirement income',
  OTHER: 'Other income',
};

export const careRecipientLabels = {
  SURVIVING_SPOUSE: 'Surviving spouse',
  OTHER: 'Other',
};

export const incomeSourceLabels = {
  NO_INCOME: 'No income',
  ONE_TO_FOUR_SOURCES: '1-4 sources of income',
  MORE_THAN_FIVE_SOURCES: '5+ sources of income',
};

export const medicalExpenseTypeLabels = {
  ADAPTIVE_MEDICAL_EQUIPMENT: { label: 'Adaptive medical equipment' },
  AMBULANCE: { label: 'Ambulance' },
  BURIAL_FOR_THE_VETERAN: { label: 'Burial for the Veteran' },
  DENTAL_COPAYS_AND_FEES: { label: 'Dental copays and fees' },
  DENTURES: { label: 'Dentures' },
  HEARING_AIDS_AND_BATTERIES: { label: 'Hearing aids and batteries' },
  HOSPITAL_COPAYS_AND_FEES: { label: 'Hospital copays and fees' },
  INCONTINENCE_SUPPLIES: { label: 'Incontinence supplies' },
  LONG_TERM_CARE_INSURANCE: { label: 'Long-term care insurance' },
  MEDICAL_COPAYS_AND_FEES: { label: 'Medical copays and fees' },
  MEDICAL_SUPPLIES: { label: 'Medical supplies' },
  MEDICARE_PART_B: { label: 'Medicare Part B' },
  MEDICARE_PART_C: { label: 'Medicare Part C' },
  MEDICARE_PART_D: { label: 'Medicare Part D' },
  OVER_THE_COUNTER_MEDICATION: { label: 'Over-the-counter medication' },
  PRESCRIPTION_MEDICATION: { label: 'Prescription medication' },
  PRIVATE_MEDICAL_INSURANCE_PREMIUMS: {
    label: 'Private medical insurance premiums',
  },
  VISION_CARE_COPAYS_AND_FEES: { label: 'Vision care copays and fees' },
  VITAMIN_FOOD_SUPPLEMENTS_AND_HERBAL_REMEDIES: {
    label: 'Vitamin, food supplements, and herbal remedies',
  },
};

// Get military states to filter them out
// Marriage-specific state list: filter out military state codes so the
// marriage pages always render the US states/territories dropdown instead
// of the military AA/AE/AP radio. We do this here at the page level so we
// don't change global address behavior.
export const MARRIAGE_MILITARY_STATE_VALUES = constants.militaryStates
  ? constants.militaryStates.map(state => state.value)
  : ['AA', 'AE', 'AP'];

export const MARRIAGE_FILTERED_STATES = constants.states.USA.filter(
  state => !MARRIAGE_MILITARY_STATE_VALUES.includes(state.value),
);
export const STATE_VALUES = MARRIAGE_FILTERED_STATES.map(s => s.value);
export const STATE_NAMES = MARRIAGE_FILTERED_STATES.map(s => s.label);

export const COUNTRY_VALUES = constants.countries
  .filter(country => country.value !== 'USA')
  .map(country => country.value);
export const COUNTRY_NAMES = constants.countries
  .filter(country => country.value !== 'USA')
  .map(country => country.label);

export const filteredStatesForEnd = constants.states.USA.filter(
  s => !MARRIAGE_MILITARY_STATE_VALUES.includes(s.value),
);

export const childHouseholdLabels = {
  YES: 'The child lives with me',
  NO: 'The child does not live with me',
  ONLY: 'I’m their only dependent child',
};

export const childRelationshipLabels = {
  BIOLOGICAL: 'They’re the Veteran’s biological child',
  ADOPTED: 'They’re the Veteran’s adopted child',
  STEPCHILD: 'They’re the Veteran’s stepchild',
};
